/**
 * Cause-chain capture: walks `Error.cause` (and `AggregateError.errors`) so a
 * wrapped error ships with the underlying root cause, the way Sentry's
 * linkedErrors integration does.
 *
 * Adapted from sentry-javascript (packages/core/src/utils/aggregate-errors.ts),
 * MIT License, Copyright (c) 2019-present Functional Software, Inc. dba Sentry.
 *
 * Pure and dependency-free; covered by __tests__/linked-errors.test.ts.
 */

import { parseStack, StackFrame } from './stack-parser';

export interface LinkedException {
    exceptionType: string;
    value: string;
    stackFrames: StackFrame[];
    /** How this exception is linked to its parent: 'cause' or 'errors[i]'. */
    source: string;
}

const DEFAULT_LIMIT = 5;

function isErrorLike(value: unknown): value is Error {
    return (
        value instanceof Error ||
        (typeof value === 'object' &&
            value !== null &&
            typeof (value as { message?: unknown }).message === 'string' &&
            typeof (value as { name?: unknown }).name === 'string')
    );
}

function toLinkedException(error: Error, source: string): LinkedException {
    return {
        exceptionType: error.name || 'Error',
        value: error.message || '',
        stackFrames: parseStack(typeof error.stack === 'string' ? error.stack : null),
        source,
    };
}

/**
 * Collect the chain of linked exceptions hanging off a thrown value, in
 * outermost-cause-first order. Cycle-safe and bounded by `limit`. Returns []
 * for non-Error values or errors with no cause.
 */
export function collectLinkedErrors(error: unknown, limit: number = DEFAULT_LIMIT): LinkedException[] {
    const out: LinkedException[] = [];
    if (!isErrorLike(error)) {
        return out;
    }
    const seen = new Set<unknown>([error]);

    const visit = (parent: Error): void => {
        if (out.length >= limit) {
            return;
        }
        const cause = (parent as Error & { cause?: unknown }).cause;
        if (isErrorLike(cause) && !seen.has(cause)) {
            seen.add(cause);
            out.push(toLinkedException(cause, 'cause'));
            visit(cause);
        }
        const children = (parent as Error & { errors?: unknown }).errors;
        if (Array.isArray(children)) {
            for (let i = 0; i < children.length && out.length < limit; i += 1) {
                const child = children[i];
                if (isErrorLike(child) && !seen.has(child)) {
                    seen.add(child);
                    out.push(toLinkedException(child, `errors[${i}]`));
                    visit(child);
                }
            }
        }
    };

    visit(error);
    return out;
}
